import { useMemo, useState } from "react";
import { ChevronDown, Columns2, Plus, Search, SlidersHorizontal } from "lucide-react";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import type { TagRecord, TableRecord, ScheduleColumnOption } from "@/types/takeoff-pilot";
import { MultiSelect, type MultiSelectSection } from "@/components/ui/multi-select";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  SearchDocumentTagsDialog,
  type DocumentTagSearchResponse,
} from "@/features/takeoff-pilot/search-document-tags-dialog";
import { AddCustomLineItemDialog } from "./add-custom-line-item-dialog";
import { AddCustomScheduleColumnDialog } from "./add-custom-schedule-column-dialog";

const RESULT_COLUMN_OPTIONS = [
  { id: "result:count", value: "count", label: "Count" },
  { id: "result:lf", value: "lf", label: "LF" },
  { id: "result:sqft", value: "sqft", label: "SQFT" },
  { id: "result:cf", value: "cf", label: "CF" },
] as const;

function toResultColumnId(value: (typeof RESULT_COLUMN_OPTIONS)[number]["value"]) {
  return `result:${value}`;
}

function toScheduleColumnId(value: string) {
  return `schedule:${value}`;
}

function fromResultColumnId(value: string) {
  return value.replace(/^result:/, "") as (typeof RESULT_COLUMN_OPTIONS)[number]["value"];
}

function fromScheduleColumnId(value: string) {
  return value.replace(/^schedule:/, "");
}

interface PanelHeaderProps {
  onCreateCustomLineItem: (args: {
    rawValue: string;
    unitCost: number | null;
  }) => Promise<void>;
  onSearchDocumentTags: (query: string) => Promise<DocumentTagSearchResponse>;
  onCreateCustomLineItemsFromDocumentTags: (
    documentTagIds: Id<"documentIntelligenceCanonicalTags">[]
  ) => Promise<void>;
  canManageCustomLineItems: boolean;
  onCreateCustomScheduleColumn: (header: string) => Promise<void>;
  canManageCustomScheduleColumns: boolean;
  tags: TagRecord[];
  tables: TableRecord[];
  selectedTagIds: string[];
  onSelectTag: (tagId: string) => void;
  onRemoveTag: (tagId: string) => void;
  isCounting: boolean;
  onDropdownSelect?: (tagId: string) => void;
  onDropdownRemove?: (tagId: string) => void;
  onDropdownOpenChange?: (open: boolean) => void;
  pendingSelections?: Map<string, boolean>;
  hasSchedule: boolean;
  availableScheduleColumns: ScheduleColumnOption[];
  selectedScheduleHeaders: string[];
  onSelectScheduleHeader: (header: string) => void;
  onRemoveScheduleHeader: (header: string) => void;
  selectedQuantityColumns: string[];
  onSelectQuantityColumn: (column: string) => void;
  onRemoveQuantityColumn: (column: string) => void;
  costColumnsVisible: boolean;
  onCostColumnsVisibleChange: (visible: boolean) => void;
  onHideResults: () => void;
}

export function PanelHeader({
  onCreateCustomLineItem,
  onSearchDocumentTags,
  onCreateCustomLineItemsFromDocumentTags,
  canManageCustomLineItems,
  onCreateCustomScheduleColumn,
  canManageCustomScheduleColumns,
  tags,
  tables,
  selectedTagIds,
  onSelectTag,
  onRemoveTag,
  isCounting,
  onDropdownSelect,
  onDropdownRemove,
  onDropdownOpenChange,
  pendingSelections,
  hasSchedule,
  availableScheduleColumns,
  selectedScheduleHeaders,
  onSelectScheduleHeader,
  onRemoveScheduleHeader,
  selectedQuantityColumns,
  onSelectQuantityColumn,
  onRemoveQuantityColumn,
  costColumnsVisible,
  onCostColumnsVisibleChange,
  onHideResults,
}: PanelHeaderProps) {
  const [isCustomLineItemDialogOpen, setIsCustomLineItemDialogOpen] = useState(false);
  const [isSearchDocumentTagsDialogOpen, setIsSearchDocumentTagsDialogOpen] = useState(false);
  const [isCustomScheduleColumnDialogOpen, setIsCustomScheduleColumnDialogOpen] = useState(false);
  const sections: MultiSelectSection[] = useMemo(
    () =>
      tables.map((table) => ({
        id: table._id,
        label: table.name,
        description: table.isCustom ?? false ? "Manual line items" : `Page ${table.pageNumber}`,
        options: tags
          .filter((tag) => tag.sourceTableId === table._id)
          .map((tag) => ({
            value: tag._id,
            label: tag.rawValue,
          })),
      })),
    [tables, tags]
  );
  const scheduleColumnSections: MultiSelectSection[] = useMemo(
    () => [
      {
        id: "result-columns",
        label: "Result Columns",
        options: RESULT_COLUMN_OPTIONS.map((column) => ({
          value: column.id,
          label: column.label,
        })),
      },
      {
        id: "schedule-columns",
        label: hasSchedule ? "Schedule Columns" : "Custom Columns",
        options: availableScheduleColumns.map((column) => ({
          value: toScheduleColumnId(column.value),
          label: column.label,
        })),
      },
    ],
    [availableScheduleColumns, hasSchedule]
  );
  const resultColumnValues = useMemo(
    () => new Set<string>(RESULT_COLUMN_OPTIONS.map((column) => column.id)),
    []
  );

  const effectiveSelectedTagIds = useMemo(() => {
    const baseIds = new Set<string>(selectedTagIds);
    if (pendingSelections) {
      for (const [tagId, included] of pendingSelections) {
        if (included) {
          baseIds.add(tagId);
        } else {
          baseIds.delete(tagId);
        }
      }
    }
    return Array.from(baseIds);
  }, [selectedTagIds, pendingSelections]);

  const handleSelectAll = (tableId: string) => {
    const tableTags = tags.filter((tag) => tag.sourceTableId === tableId);
    for (const tag of tableTags) {
      onDropdownSelect?.(String(tag._id));
    }
  };

  const handleDeselectAll = (tableId: string) => {
    const tableTags = tags.filter((tag) => tag.sourceTableId === tableId);
    for (const tag of tableTags) {
      onDropdownRemove?.(String(tag._id));
    }
  };

  const handleColumnSelect = (value: string) => {
    if (resultColumnValues.has(value)) {
      onSelectQuantityColumn(fromResultColumnId(value));
      return;
    }
    onSelectScheduleHeader(fromScheduleColumnId(value));
  };

  const handleColumnRemove = (value: string) => {
    if (resultColumnValues.has(value)) {
      onRemoveQuantityColumn(fromResultColumnId(value));
      return;
    }
    onRemoveScheduleHeader(fromScheduleColumnId(value));
  };

  const handleSelectAllColumns = (sectionId: string) => {
    if (sectionId === "result-columns") {
      for (const column of RESULT_COLUMN_OPTIONS) {
        onSelectQuantityColumn(column.value);
      }
      return;
    }

    for (const column of availableScheduleColumns) {
      onSelectScheduleHeader(column.value);
    }
  };

  const handleDeselectAllColumns = (sectionId: string) => {
    if (sectionId === "result-columns") {
      for (const column of RESULT_COLUMN_OPTIONS) {
        onRemoveQuantityColumn(column.value);
      }
      return;
    }

    for (const header of selectedScheduleHeaders) {
      onRemoveScheduleHeader(header);
    }
  };

  return (
    <div className="flex items-center justify-between gap-3 border-b border-[#333] px-3 py-2">
      <div className="flex min-w-0 items-center gap-2">
        <h3 className="shrink-0 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
          Tag Results
        </h3>
        <MultiSelect
          sections={sections}
          selected={effectiveSelectedTagIds}
          onSelect={onDropdownSelect ?? onSelectTag}
          onRemove={onDropdownRemove ?? onRemoveTag}
          onSelectAll={handleSelectAll}
          onDeselectAll={handleDeselectAll}
          onOpenChange={onDropdownOpenChange}
          placeholder="Add tags..."
          triggerAriaLabel="Selected tags"
          disabled={isCounting}
          triggerWidth="w-[150px]"
          triggerLabel="Tags"
          triggerIcon={<SlidersHorizontal className="size-3.5" />}
          showSelectedBadges={false}
          dropdownFooter={
            canManageCustomLineItems ? (
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="justify-start border border-[#333] bg-background text-xs shadow-none hover:bg-muted/60"
                  onClick={() => setIsCustomLineItemDialogOpen(true)}
                >
                  <Plus className="mr-2 size-4" />
                  Custom Item
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="justify-start border border-[#333] bg-background text-xs shadow-none hover:bg-muted/60"
                  onClick={() => setIsSearchDocumentTagsDialogOpen(true)}
                >
                  <Search className="mr-2 size-4" />
                  Search Tags
                </Button>
              </div>
            ) : null
          }
        />
        <MultiSelect
          sections={scheduleColumnSections}
          selected={[
            ...selectedQuantityColumns.map((column) =>
              toResultColumnId(column as (typeof RESULT_COLUMN_OPTIONS)[number]["value"])
            ),
            ...selectedScheduleHeaders.map((header) => toScheduleColumnId(header)),
          ]}
          onSelect={handleColumnSelect}
          onRemove={handleColumnRemove}
          onSelectAll={handleSelectAllColumns}
          onDeselectAll={handleDeselectAllColumns}
          placeholder="Visible columns..."
          triggerAriaLabel="Visible columns"
          disabled={isCounting}
          triggerWidth="w-[220px]"
          dropdownWidth="w-[640px]"
          columns="auto-fit"
          optionMinWidth="11rem"
          triggerLabel="Columns"
          triggerIcon={<Columns2 className="size-3.5" />}
          showSelectedBadges={false}
          wrapOptionLabels
          dropdownFooter={
            canManageCustomScheduleColumns ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full justify-start border border-[#333] bg-background text-xs shadow-none hover:bg-muted/60"
                onClick={() => setIsCustomScheduleColumnDialogOpen(true)}
              >
                <Plus className="mr-2 size-4" />
                Add Column
              </Button>
            ) : null
          }
        />
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <div className="flex h-8 items-center gap-2 rounded-md border border-[#333] bg-background px-2.5">
          <Switch
            checked={costColumnsVisible}
            onCheckedChange={onCostColumnsVisibleChange}
            aria-label="Show cost columns"
          />
          <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
            Costs
          </span>
        </div>
        <Button
          type="button"
          variant="outline"
          size="xs"
          className="border border-[#333] bg-background px-2.5 text-[11px] shadow-none hover:bg-muted/60"
          onClick={onHideResults}
        >
          <ChevronDown className="size-3.5" />
          Hide Results
        </Button>
      </div>
      <AddCustomLineItemDialog
        open={isCustomLineItemDialogOpen}
        onOpenChange={setIsCustomLineItemDialogOpen}
        onCreateCustomLineItem={onCreateCustomLineItem}
      />
      <SearchDocumentTagsDialog
        open={isSearchDocumentTagsDialogOpen}
        onOpenChange={setIsSearchDocumentTagsDialogOpen}
        onSearchDocumentTags={onSearchDocumentTags}
        onCreateCustomLineItemsFromDocumentTags={onCreateCustomLineItemsFromDocumentTags}
      />
      <AddCustomScheduleColumnDialog
        open={isCustomScheduleColumnDialogOpen}
        onOpenChange={setIsCustomScheduleColumnDialogOpen}
        onCreateCustomScheduleColumn={onCreateCustomScheduleColumn}
      />
    </div>
  );
}
